import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { difference, isEqual } from 'lodash-es';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { ILogger } from '@nimbus-library/features/logger/services/logger/i-logger';
import { IPermissionManagerService } from '@nimbus-library/features/user-settings/services/contracts/i-permission-manager.service';
import { GlobalPermissionMapper } from '@nimbus-global/features/permissions/models/global-permission.mapper';
import { GlobalPermissionsModel } from '@nimbus-global/features/permissions/models/global-permissions.model';
import { PermissionsModel } from '@nimbus-global/features/permissions/models/permissions.model';
import { NimbusPermission } from '@nimbus-global/features/permissions/models/enums/nimbus-permission.enum';
import { WallboardSelectors } from '../../store/wallboard.selectors';
import { NimbusWallboardSignalRHub } from '../signalr/nimbus-wallboard-signalr.hub';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class WallboardPermissionManagerService extends IPermissionManagerService {
  private readonly globalPermissionsModel$: BehaviorSubject<GlobalPermissionsModel> = new BehaviorSubject<GlobalPermissionsModel>(null);
  private readonly permissionsModel$: BehaviorSubject<PermissionsModel> = new BehaviorSubject<PermissionsModel>(PermissionsModel.Empty);

  public constructor(
    private readonly store: Store,
    private readonly nimbusWallboardSignalRHub: NimbusWallboardSignalRHub,
    private readonly logger: ILogger,
  ) {
    super();
  }

  public initialize(): void {
    this.store
      .select(WallboardSelectors.bootstrap)
      .pipe(
        filter((bootstrap) => !!bootstrap),
        untilDestroyed(this),
      )
      .subscribe((bootstrap) => {
        const globalPermissionsModel = new GlobalPermissionMapper(this.logger).handle(bootstrap.permissionModel);
        const permissionsModel = globalPermissionsModel.toPermissionModel();
        this.adjustSignalRGroupsWithTeamId(permissionsModel, this.permissionsModel$.getValue());
        this.globalPermissionsModel$.next(globalPermissionsModel);
        this.permissionsModel$.next(permissionsModel);
      });
    this.nimbusWallboardSignalRHub.isConnected$
      .pipe(
        filter((isConnected) => !!isConnected),
        untilDestroyed(this),
      )
      .subscribe(() => this.adjustSignalRGroupsWithTeamId(this.getPermissionsModel(), null));
  }

  public reloadGlobalPermissionsModel(): Observable<GlobalPermissionsModel> {
    return of(this.getGlobalPermissionsModel());
  }

  public reloadPermissionsModel(): Observable<PermissionsModel> {
    return of(this.getPermissionsModel());
  }

  public getGlobalPermissionsModel$(): Observable<GlobalPermissionsModel> {
    return this.globalPermissionsModel$.pipe(distinctUntilChanged(isEqual));
  }

  public getGlobalPermissionsModel(): GlobalPermissionsModel {
    return this.globalPermissionsModel$.getValue();
  }

  public getPermissionsModel$(): Observable<PermissionsModel> {
    return this.permissionsModel$.pipe(distinctUntilChanged(isEqual));
  }

  public getPermissionsModel(): PermissionsModel {
    return this.permissionsModel$.getValue();
  }

  public getTenantIds(): string[] {
    return Object.keys(this.getPermissionsModel()?.tenants || {});
  }

  public getTeamIds$(nimbusPermission: NimbusPermission): Observable<string[]> {
    return this.getPermissionsModel$().pipe(map((model) => this.getTeamIdsByPermission(model, nimbusPermission)));
  }

  public getTeamIds(nimbusPermission: NimbusPermission): string[] {
    return this.getTeamIdsByPermission(this.getPermissionsModel(), nimbusPermission);
  }

  public getBelongedTeamIds$(): Observable<string[]> {
    return of([]);
  }

  public getBelongedTeamIds(): string[] {
    return [];
  }

  public getOwnedTeamIds$(): Observable<string[]> {
    return of([]);
  }

  public getOwnedTeamIds(): string[] {
    return [];
  }

  public getSkillBasedTeamIds$(): Observable<string[]> {
    return of([]);
  }

  public getSkillBasedTeamIds(): string[] {
    return [];
  }

  private getTeamIdsByPermission(model: PermissionsModel, nimbusPermission: NimbusPermission): string[] {
    return model ? Object.keys(model.teams).filter((teamId) => model.hasTeamPermission(teamId, nimbusPermission)) : [];
  }

  private adjustSignalRGroupsWithTeamId(model: PermissionsModel, previousModel: PermissionsModel): void {
    if (!this.nimbusWallboardSignalRHub.isConnected$.getValue()) {
      return;
    }

    const nimbusPermission = NimbusPermission.ReadConversationsInProgress;
    const teamIds = this.getTeamIdsByPermission(model, nimbusPermission);
    const previousTeamIds = this.getTeamIdsByPermission(previousModel, nimbusPermission);

    const addedTeamIds = difference(teamIds, previousTeamIds);
    if (addedTeamIds.length) {
      this.nimbusWallboardSignalRHub.addToGroupsWithTeamId(addedTeamIds, nimbusPermission);
    }

    const removedTeamIds = difference(previousTeamIds, teamIds);
    if (removedTeamIds.length) {
      this.nimbusWallboardSignalRHub.removeFromGroupsWithTeamId(removedTeamIds, nimbusPermission);
    }
  }
}
